import { Fragment, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import {
  defectueuseSum,
  GRADE_LIMITS,
  REFRACTION_BANDS,
  type ClassificationInputs,
  type ClassificationMetric,
  type RefractionInputs,
} from "@/features/intake/lib/quality-rules"
import { formatNumber } from "@/lib/format"

// QualityMathDialog opens a wide modal that walks the analyst through
// every step of the refraction kg calculation and the worst-of grade
// decision. The modal uses a horizontal column layout so the math reads
// left-to-right; embedding it inline next to the lab card was too cramped
// (see prior iteration).
//
// Math semantics (mirrors backend rules.go):
//   refraction_kg(metric) = max(0, value − cleanCeiling) × accepted_kg / 100
//   total = sum across the four metrics with deduction zones
//   defectueuse = germinated + mites + flatBeans
//   grade = worst-of(mouldy, slaty, defectueuse) against G1/G2 brackets

const REFRACTION_METRICS = [
  "moisture",
  "foreignMatter",
  "broken",
  "waste",
  "crabot",
  "mouldy",
] as const

const GRADE_DRIVERS: ClassificationMetric[] = ["mouldy", "slaty", "defectueuse"]

function localePct(n: number, decimals = 2): string {
  return formatNumber(n, decimals)
}

function localeKg(n: number, decimals = 2): string {
  return formatNumber(n, decimals)
}

interface QualityMathDialogProps {
  refraction: RefractionInputs
  classification: ClassificationInputs
  acceptedKg: number
  // Inline trigger button. Caller passes a button-shaped element.
  trigger?: React.ReactNode
}

export function QualityMathDialog({
  refraction,
  classification,
  acceptedKg,
  trigger,
}: QualityMathDialogProps) {
  const { t } = useTranslation("intake")
  const [open, setOpen] = useState(false)

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button type="button" variant="outline" size="sm">
            {t("math.toggle")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-6xl xl:max-w-7xl">
        <DialogHeader>
          <DialogTitle>{t("math.dialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("math.dialogSubtitle", { kg: formatNumber(acceptedKg) })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-6">
          <RefractionSection refraction={refraction} acceptedKg={acceptedKg} />
          <GradeSection classification={classification} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface RefractionSectionProps {
  refraction: RefractionInputs
  acceptedKg: number
}

function RefractionSection({ refraction, acceptedKg }: RefractionSectionProps) {
  const { t } = useTranslation("intake")

  const rows = REFRACTION_METRICS.map((metric) => {
    const value = Number(refraction[metric]) || 0
    const band = REFRACTION_BANDS[metric]
    const inRefused = value > band.reject
    // Crabot and mouldy are pass/fail only — no proportional deduction
    // band even when the value crosses the clean ceiling. Tag them so
    // the row labels them as such instead of pretending there's a
    // ceiling-vs-value comparison.
    const hasBand = metric !== "crabot" && metric !== "mouldy"
    const excess = hasBand ? Math.max(0, value - band.clean) : 0
    const kg = inRefused || !hasBand ? 0 : (excess * acceptedKg) / 100
    return {
      metric,
      value,
      ceiling: band.clean,
      reject: band.reject,
      excess,
      kg,
      inRefused,
      hasBand,
      isClean: !inRefused && excess === 0,
    }
  })

  const total = rows.reduce((s, r) => s + (r.inRefused ? 0 : r.kg), 0)

  return (
    <section>
      <header className="mb-3">
        <h3 className="text-sm font-semibold">{t("math.refractionTitle")}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("math.refractionFormula")}
        </p>
      </header>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-muted/40">
            <tr className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
              <th className="px-3 py-2 text-left">{t("math.col.metric")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.value")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.ceiling")}</th>
              <th className="px-3 py-2 text-right">
                {t("math.col.deduction")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <RefractionTableRow
                key={r.metric}
                row={r}
                acceptedKg={acceptedKg}
              />
            ))}
            <tr className="border-t-2 border-border bg-muted/30">
              <td
                colSpan={3}
                className="px-3 py-2 text-xs font-medium tracking-wide text-muted-foreground uppercase"
              >
                {t("math.total")}
              </td>
              <td className="px-3 py-2 text-right text-base font-semibold whitespace-nowrap tabular-nums">
                −{localeKg(total)} kg
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}

interface RefractionRow {
  metric: (typeof REFRACTION_METRICS)[number]
  value: number
  ceiling: number
  reject: number
  excess: number
  kg: number
  inRefused: boolean
  isClean: boolean
  hasBand: boolean
}

function RefractionTableRow({
  row: r,
  acceptedKg,
}: {
  row: RefractionRow
  acceptedKg: number
}) {
  const { t } = useTranslation("intake")

  return (
    <>
      <tr
        className={cn(
          "border-t border-border/60",
          r.inRefused && "bg-red-50/60 dark:bg-red-500/10"
        )}
      >
        <td className="px-3 py-2 align-top">
          <div className="font-medium">{t(`lab.fields.${r.metric}`)}</div>
          {!r.hasBand ? (
            <div className="mt-0.5 text-[11px] text-muted-foreground">
              {t("math.passOnly", { limit: localePct(r.reject) })}
            </div>
          ) : null}
        </td>
        <td className="px-3 py-2 text-right align-top whitespace-nowrap tabular-nums">
          {localePct(r.value)} %
        </td>
        <td className="px-3 py-2 text-right align-top whitespace-nowrap text-muted-foreground tabular-nums">
          {r.hasBand ? `${localePct(r.ceiling)} %` : "—"}
        </td>
        <td className="px-3 py-2 text-right align-top whitespace-nowrap">
          {r.inRefused ? (
            <span className="text-xs font-semibold text-red-700 uppercase dark:text-red-300">
              {t("math.refused")}
            </span>
          ) : r.isClean || !r.hasBand ? (
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
              {t("math.clean")}
            </span>
          ) : (
            <span className="font-semibold tabular-nums">
              −{localeKg(r.kg)} kg
            </span>
          )}
        </td>
      </tr>
      {!r.inRefused && r.hasBand && !r.isClean ? (
        <tr className="bg-muted/20">
          <td
            colSpan={4}
            className="border-t border-border/40 px-3 py-1.5 text-[11px] text-muted-foreground tabular-nums"
          >
            <span className="font-medium text-foreground">
              {t(`lab.fields.${r.metric}`)}
            </span>
            {": ("}
            {localePct(r.value)} % − {localePct(r.ceiling)} %{") × "}
            {formatNumber(acceptedKg)} kg ÷ 100 ={" "}
            <span className="text-amber-700 dark:text-amber-300">
              +{localePct(r.excess)} pts
            </span>
            {" → "}
            <span className="font-semibold text-foreground">
              {localeKg(r.kg)} kg
            </span>
          </td>
        </tr>
      ) : null}
      {r.inRefused ? (
        <tr className="bg-red-50/40 dark:bg-red-500/5">
          <td
            colSpan={4}
            className="border-t border-red-200/60 px-3 py-1.5 text-[11px] text-red-700 dark:border-red-500/30 dark:text-red-300"
          >
            {localePct(r.value)} % &gt; {localePct(r.reject)} % —{" "}
            {t("math.rejectExplain")}
          </td>
        </tr>
      ) : null}
    </>
  )
}

interface GradeSectionProps {
  classification: ClassificationInputs
}

function GradeSection({ classification }: GradeSectionProps) {
  const { t } = useTranslation("intake")

  const defectueuse = defectueuseSum(classification)

  function valueOf(driver: ClassificationMetric): number {
    if (driver === "defectueuse") return defectueuse
    return Number(classification[driver]) || 0
  }

  function bracket(
    driver: ClassificationMetric,
    value: number
  ): "G1" | "G2" | "SG" {
    const lim = GRADE_LIMITS[driver]
    if (value > lim.g2) return "SG"
    if (value > lim.g1) return "G2"
    return "G1"
  }

  const rows = GRADE_DRIVERS.map((driver) => {
    const value = valueOf(driver)
    return { driver, value, bracket: bracket(driver, value) }
  })
  const worst: "G1" | "G2" | "SG" = rows.some((r) => r.bracket === "SG")
    ? "SG"
    : rows.some((r) => r.bracket === "G2")
      ? "G2"
      : "G1"
  const limitingDrivers = new Set(
    rows
      .filter((r) => r.bracket === worst && worst !== "G1")
      .map((r) => r.driver)
  )

  const bracketTone: Record<"G1" | "G2" | "SG", string> = {
    G1: "text-emerald-700 dark:text-emerald-300",
    G2: "text-amber-700 dark:text-amber-300",
    SG: "text-red-700 dark:text-red-300",
  }
  const bracketLabel: Record<"G1" | "G2" | "SG", string> = {
    G1: t("grade.GRADE_1"),
    G2: t("grade.GRADE_2"),
    SG: t("grade.SOUS_GRADE"),
  }

  return (
    <section className="w-160">
      <header className="mb-3">
        <h3 className="text-sm font-semibold">{t("math.gradeTitle")}</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {t("math.gradeFormula")}
        </p>
      </header>

      <div className="overflow-hidden rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-muted/40">
            <tr className="text-[10px] font-semibold tracking-wide text-muted-foreground uppercase">
              <th className="px-3 py-2 text-left">{t("math.col.driver")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.value")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.g1")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.g2")}</th>
              <th className="px-3 py-2 text-right">{t("math.col.bracket")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const lim = GRADE_LIMITS[r.driver]
              const isLimit = limitingDrivers.has(r.driver)
              return (
                <Fragment key={r.driver}>
                  <tr
                    className={cn(
                      "border-t border-border/60",
                      isLimit && "bg-amber-50/60 dark:bg-amber-500/5"
                    )}
                  >
                    <td className="px-3 py-2 align-top font-medium">
                      {t(`lab.fields.${r.driver}`)}
                    </td>
                    <td className="px-3 py-2 text-right align-top whitespace-nowrap tabular-nums">
                      {localePct(r.value)} %
                    </td>
                    <td className="px-3 py-2 text-right align-top whitespace-nowrap text-muted-foreground tabular-nums">
                      ≤ {localePct(lim.g1)} %
                    </td>
                    <td className="px-3 py-2 text-right align-top whitespace-nowrap text-muted-foreground tabular-nums">
                      ≤ {localePct(lim.g2)} %
                    </td>
                    <td
                      className={cn(
                        "px-3 py-2 text-right align-top text-sm font-semibold whitespace-nowrap",
                        bracketTone[r.bracket]
                      )}
                    >
                      {bracketLabel[r.bracket]}
                    </td>
                  </tr>
                  {r.driver === "defectueuse" ? (
                    <tr className="bg-muted/20">
                      <td
                        colSpan={5}
                        className="border-t border-border/40 px-3 py-1.5 text-[11px] text-muted-foreground tabular-nums"
                      >
                        <span className="font-medium text-foreground">
                          {t(`lab.fields.defectueuse`)} ({localePct(r.value)} %)
                        </span>
                        {" = "}
                        {t(`lab.fields.germinated`)}{" "}
                        {localePct(classification.germinated)} % +{" "}
                        {t(`lab.fields.mites`)}{" "}
                        {localePct(classification.mites)} % +{" "}
                        {t(`lab.fields.flatBeans`)}{" "}
                        {localePct(classification.flatBeans)} %
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
            <tr className="border-t-2 border-border bg-muted/30">
              <td
                colSpan={4}
                className="px-3 py-2 text-xs font-medium tracking-wide text-muted-foreground uppercase"
              >
                {t("math.gradeResult")}
              </td>
              <td
                className={cn(
                  "px-3 py-2 text-right text-base font-semibold whitespace-nowrap",
                  bracketTone[worst]
                )}
              >
                {bracketLabel[worst]}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}
